import { PageController } from '@client/components/_core/layout/PageController';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useAntdTable } from '@client/hooks/table/useAntdTable';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Table, Typography } from 'antd';

type StripeInvoiceRow = RouterOutputs['billing']['listInvoices']['rows'][number];

const EMPTY_CELL_PLACEHOLDER = <Typography.Text type="secondary">-</Typography.Text>;

export const Route = createFileRoute('/console/$orgId/configuration/financials/billing/invoices')({
    component: Invoices,
});

function Invoices() {
    const { trpc } = useTrpc();
    const { data, isPending } = trpc.billing.listInvoices.useQuery();
    const { formatDateTime } = useFormatter();

    const { tableProps } = useAntdTable<StripeInvoiceRow>({
        rowKey: 'id',
        data: {
            rows: data?.rows,
            total: data?.total,
            loading: isPending,
        },
        columns: [
            {
                title: 'Invoice Number',
                dataIndex: 'number',
                key: 'number',
                render: (text: string | null | undefined) =>
                    text ? <Typography.Text strong>{text}</Typography.Text> : EMPTY_CELL_PLACEHOLDER,
            },
            {
                title: 'Date',
                dataIndex: 'created',
                key: 'created',
                render: (timestamp: number) => formatDateTime(new Date(timestamp * 1000), 'dd/MM/yyyy HH:mm:ss'),
            },
            {
                title: 'Amount',
                dataIndex: 'total',
                key: 'total',
                render: (amount: number) =>
                    new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    }).format(amount / 100),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string | null | undefined) => {
                    if (!status) {
                        return EMPTY_CELL_PLACEHOLDER;
                    }

                    return (
                        <StatusBadge
                            module="stripeInvoice"
                            status={status}
                            customLabel={status.charAt(0).toUpperCase() + status.slice(1)}
                        />
                    );
                },
            },
            {
                title: 'PDF',
                key: 'pdf',
                render: (_, record: StripeInvoiceRow) =>
                    record.hosted_invoice_url ? (
                        <Typography.Link href={record.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                            Open
                        </Typography.Link>
                    ) : (
                        <Typography.Text type="secondary">Unavailable</Typography.Text>
                    ),
            },
        ],
        paginationConfig: {
            pageSize: 10,
            showSizeChanger: true,
        },
    });

    return (
        <PageController route={Route}>
            <Table {...tableProps} />
        </PageController>
    );
}
